import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import { ApiFetchError } from "../../lib/api.js";
import { Badge, Button, Card, ProductImage } from "../../components/index.js";
import type {
  FbaInventoryItem,
  ItemOwner,
  PlanItem,
  PlanSummary,
  PrepDetailsItem
} from "../../types.js";
import {
  buildSetItemsPayload,
  validateSelection,
  type PlanItemSelectionRow
} from "../../lib/plan-item-select.js";
import type { StepProps } from "./step-props.js";
import { PackingModeloModal, labelOwnerLabel } from "./PackingModeloModal.js";
import { AdicionarSkuModal } from "./AdicionarSkuModal.js";

/**
 * Etapa 1 — Selecionar produtos (Design.md §6.1). Origem fixa: Prep Center
 * Bristol. Em rascunho (DRAFT + workflow não iniciado), o usuário SELECIONA
 * produtos do inventário, ajusta quantidade/etiquetagem e salva via
 * PUT /inbound-plans/:id/items. Após registrar na Amazon, fica somente leitura.
 * O "Retorno do Seller Central" (prep details) com confirmação obrigatória
 * continua por item; avança quando todos os prep details estão confirmados.
 * Cada linha mostra imagem, título e ASIN (join com o snapshot de inventário)
 * para o operador conferir antes de avançar (req 3).
 */
function toOwner(value: string | null): ItemOwner {
  return value === "AMAZON" || value === "NONE" ? value : "SELLER";
}

function itemToRow(item: PlanItem): PlanItemSelectionRow {
  return {
    msku: item.msku,
    asin: "",
    fnsku: item.fnsku ?? "",
    // Vazio de propósito: o item salvo não carrega título; o nome real vem do
    // join com o snapshot de inventário no render (req 3), com fallback no SKU.
    productName: "",
    quantity: item.quantity,
    labelOwner: toOwner(item.labelOwner),
    expiration: item.expiration ?? ""
  };
}

/**
 * Estados transitórios do sync: o backend grava PREP_DETAILS_REQUESTED antes de
 * chamar a Amazon e só então o resultado. Enquanto um SKU estiver nesses
 * estados, a resposta da Amazon ainda não chegou.
 */
const PREP_EM_TRANSITO: ReadonlySet<string> = new Set([
  "PREP_DETAILS_PENDING",
  "PREP_DETAILS_REQUESTED"
]);

/**
 * Mensagem de falha ao salvar os produtos, com o SKU e o motivo REAIS.
 *
 * O backend valida os itens com Zod e devolve `message: "Invalid request
 * payload."` (genérico, em inglês) + `fields` com a causa por índice, ex.:
 * `{"items.1.expiration": "expiration está vencida — a Amazon recusa itens com
 * validade no passado."}`. Sem traduzir esse `fields` de volta para o MSKU, o
 * operador via só o texto genérico e não sabia QUAL produto corrigir.
 */
function mensagemDeSalvamento(
  error: unknown,
  selection: readonly PlanItemSelectionRow[]
): string {
  if (error instanceof ApiFetchError && error.fields !== null) {
    const linhas: string[] = [];
    for (const [campo, mensagem] of Object.entries(error.fields)) {
      const match = /^items\.(\d+)\./.exec(campo);
      const indice = match?.[1] !== undefined ? Number(match[1]) : null;
      const sku =
        indice !== null && selection[indice] !== undefined
          ? selection[indice].msku
          : null;
      linhas.push(sku !== null ? `${sku}: ${mensagem}` : mensagem);
    }
    if (linhas.length > 0) {
      return linhas.join(" | ");
    }
  }
  return error instanceof Error ? error.message : "Falha ao salvar os produtos.";
}

/** Todos os MSKUs esperados têm prep details já respondidos pela Amazon. */
function prepDetailsResolvidos(
  items: readonly PrepDetailsItem[],
  mskus: readonly string[]
): boolean {
  const porMsku = new Map(items.map((p) => [p.msku, p]));
  return mskus.every((msku) => {
    const item = porMsku.get(msku);
    return item !== undefined && !PREP_EM_TRANSITO.has(item.status);
  });
}

export function Step1Produtos({ planId, onReadyChange }: StepProps): ReactNode {
  const { api } = useAuth();
  const [plan, setPlan] = useState<PlanSummary | null>(null);
  const [selection, setSelection] = useState<PlanItemSelectionRow[]>([]);
  const [prep, setPrep] = useState<PrepDetailsItem[]>([]);
  // Snapshot de inventário por SKU para enriquecer imagem/título/ASIN (req 3).
  const [inventory, setInventory] = useState<Map<string, FbaInventoryItem>>(
    new Map()
  );
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [selErrors, setSelErrors] = useState<string[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [modalRow, setModalRow] = useState<PlanItemSelectionRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // MSKU com confirmação em andamento (setPrepDetails assíncrono + poll).
  const [confirmando, setConfirmando] = useState<string | null>(null);
  // Busca de prep details na Amazon em andamento (listPrepDetails via task).
  const [sincronizandoPrep, setSincronizandoPrep] = useState(false);
  // Conjunto de SKUs já auto-sincronizado — evita re-disparar a cada render.
  const autoSyncKeyRef = useRef<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      // Inventário é best-effort: serve só para enriquecer o display (imagem/
      // título/ASIN). Uma falha aqui não pode derrubar a Etapa 1.
      const invPromise = api
        .getInventory()
        .then((r) => r.items)
        .catch(() => [] as FbaInventoryItem[]);
      const [planRes, itemsRes, prepRes, invItems] = await Promise.all([
        api.getPlan(planId),
        api.getItems(planId),
        api.getPrepDetails(planId),
        invPromise
      ]);
      if (alive.current) {
        setPlan(planRes.plan);
        setSelection(itemsRes.data.items.map(itemToRow));
        setPrep(prepRes.data.items);
        setInventory(new Map(invItems.map((it) => [it.sellerSku, it])));
        setDirty(false);
      }
    } catch (error: unknown) {
      if (alive.current) {
        setErro(error instanceof Error ? error.message : "Falha ao carregar os produtos.");
      }
    } finally {
      if (alive.current) {
        setCarregando(false);
      }
    }
  }, [api, planId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Poll de getPrepDetails até `pronto` (ou esgotar as tentativas). Reflete cada
   * leitura na tela para o operador ver o progresso. Devolve os itens quando
   * pronto; `null` em timeout ou desmontagem.
   */
  const pollarPrepDetails = useCallback(
    async (
      pronto: (items: PrepDetailsItem[]) => boolean,
      tentativas = 24,
      intervaloMs = 2500
    ): Promise<PrepDetailsItem[] | null> => {
      for (let i = 0; i < tentativas && alive.current; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, intervaloMs));
        if (!alive.current) {
          return null;
        }
        const res = await api.getPrepDetails(planId).catch(() => null);
        if (!alive.current) {
          return null;
        }
        if (res !== null) {
          setPrep(res.data.items);
          if (pronto(res.data.items)) {
            return res.data.items;
          }
        }
      }
      return null;
    },
    [api, planId]
  );

  /**
   * Busca na Amazon os prep details dos SKUs informados e ACOMPANHA até
   * chegarem. Sem isto a Etapa 1 é um beco sem saída: salvar os produtos não
   * trazia a preparação, e sem preparação não há o que confirmar — o botão
   * "Avançar" nunca habilitava (prod: plano fdf97c3d, 3 SKUs presos 15 min).
   * Scope MISSING: nunca desconfirma um SKU já resolvido.
   */
  const sincronizarFaltantes = useCallback(
    async (mskus: readonly string[]): Promise<void> => {
      if (mskus.length === 0) {
        return;
      }
      setSincronizandoPrep(true);
      setErro(null);
      try {
        await api.refreshPrepDetails(planId, "MISSING");
        const completo = await pollarPrepDetails((items) =>
          prepDetailsResolvidos(items, mskus)
        );
        if (completo === null && alive.current) {
          setErro(
            'A Amazon ainda não retornou a preparação de todos os SKUs. Clique em "Atualizar da Amazon" para tentar novamente.'
          );
        }
      } catch (error: unknown) {
        if (alive.current) {
          setErro(
            error instanceof Error
              ? error.message
              : "Falha ao buscar a preparação dos SKUs na Amazon."
          );
        }
      } finally {
        if (alive.current) {
          setSincronizandoPrep(false);
        }
      }
    },
    [api, planId, pollarPrepDetails]
  );

  const editable =
    plan !== null && plan.status === "DRAFT" && plan.workflowStatus === "NOT_STARTED";

  const prepByMsku = useMemo(
    () => new Map(prep.map((p) => [p.msku, p])),
    [prep]
  );
  // Cobertura de prep cruzada com a SELEÇÃO atual (não com `prep` isolado): só
  // avança quando todo SKU selecionado tem prep confirmado (§6.1 / SDD).
  const confirmedCount = selection.filter(
    (r) => prepByMsku.get(r.msku)?.confirmedAt != null
  ).length;
  const allConfirmed =
    selection.length > 0 && confirmedCount === selection.length;
  const totalUnits = selection.reduce((a, r) => a + r.quantity, 0);
  const ready = !dirty && allConfirmed;

  useEffect(() => {
    onReadyChange(ready);
  }, [ready, onReadyChange]);

  // SKU salvo sem prep details = Etapa 1 travada (nada a confirmar ⇒ "Avançar"
  // desabilitado para sempre). Busca a preparação na Amazon automaticamente,
  // uma vez por conjunto de faltantes (o ref evita re-disparo a cada render).
  useEffect(() => {
    if (carregando || !editable || dirty || sincronizandoPrep) {
      return;
    }
    const faltantes = selection
      .map((r) => r.msku)
      .filter((msku) => {
        const p = prepByMsku.get(msku);
        return p === undefined || PREP_EM_TRANSITO.has(p.status);
      })
      .sort();
    if (faltantes.length === 0) {
      return;
    }
    const chave = faltantes.join("|");
    if (autoSyncKeyRef.current === chave) {
      return;
    }
    autoSyncKeyRef.current = chave;
    void sincronizarFaltantes(faltantes);
  }, [
    carregando,
    editable,
    dirty,
    sincronizandoPrep,
    selection,
    prepByMsku,
    sincronizarFaltantes
  ]);

  function patchRow(msku: string, patch: Partial<PlanItemSelectionRow>): void {
    setSelection((prev) => prev.map((r) => (r.msku === msku ? { ...r, ...patch } : r)));
    setDirty(true);
  }

  function removeRow(msku: string): void {
    setSelection((prev) => prev.filter((r) => r.msku !== msku));
    setDirty(true);
  }

  function addRows(rows: PlanItemSelectionRow[]): void {
    const existing = new Set(selection.map((r) => r.msku));
    const fresh = rows.filter((r) => !existing.has(r.msku));
    if (fresh.length === 0) {
      return;
    }
    setSelection((prev) => [...prev, ...fresh]);
    setDirty(true);
  }

  async function salvar(): Promise<void> {
    const errors = validateSelection(selection);
    if (errors.length > 0) {
      setSelErrors(errors);
      return;
    }
    setSelErrors([]);
    setSaving(true);
    setErro(null);
    try {
      await api.setPlanItems(planId, buildSetItemsPayload(selection, prepByMsku));
      // Recarrega prep para refletir os SKUs novos/alterados (e confirmações
      // possivelmente invalidadas pelo backend).
      const prepRes = await api.getPrepDetails(planId);
      if (alive.current) {
        setPrep(prepRes.data.items);
        setDirty(false);
      }
    } catch (error: unknown) {
      if (alive.current) {
        setErro(mensagemDeSalvamento(error, selection));
      }
    } finally {
      if (alive.current) {
        setSaving(false);
      }
    }
  }

  // Itens com prepCategory UNKNOWN não têm classificação de preparo registrada
  // na Amazon: a confirmação local pura deixaria o createInboundPlan falhar com
  // FBA_INB_0182 ("Prep classification for this SKU was missing"). Para eles a
  // confirmação passa por setPrepDetails (assíncrono) e o item é confirmado pela
  // reconciliação do backend — aqui fazemos poll até o `confirmedAt` aparecer.
  async function confirmar(msku: string): Promise<void> {
    setErro(null);
    setConfirmando(msku);
    try {
      const p = prepByMsku.get(msku) ?? null;
      if (p !== null && p.prepCategory === "UNKNOWN") {
        await api.submitPrepDetails(planId, msku);
        const ok = await aguardarConfirmacao(msku);
        if (!ok && alive.current) {
          setErro(
            "A Amazon ainda está processando a classificação de preparo deste SKU. Aguarde alguns segundos e clique em \"Confirmar preparação\" novamente."
          );
        }
      } else {
        const res = await api.confirmPrep(planId, msku);
        if (alive.current) {
          setPrep((prev) => prev.map((p2) => (p2.msku === msku ? res.data.item : p2)));
        }
      }
    } catch (error: unknown) {
      if (alive.current) {
        setErro(error instanceof Error ? error.message : "Falha ao confirmar a preparação.");
      }
    } finally {
      if (alive.current) {
        setConfirmando(null);
      }
    }
  }

  /**
   * Poll pós-setPrepDetails: a reconciliação do backend confirma o item quando a
   * operação SUCCESS chega da Amazon (segundos, em geral). Retorna true quando o
   * `confirmedAt` aparece; false em timeout (o processamento continua no backend
   * — reclicar em "Confirmar preparação" apenas reconsulta).
   */
  async function aguardarConfirmacao(msku: string): Promise<boolean> {
    for (let tentativa = 0; tentativa < 20 && alive.current; tentativa += 1) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const res = await api.getPrepDetails(planId);
      if (!alive.current) {
        return false;
      }
      setPrep(res.data.items);
      const item = res.data.items.find((p) => p.msku === msku) ?? null;
      if (item?.confirmedAt != null) {
        return true;
      }
      if (item?.status === "PREP_DETAILS_FAILED") {
        setErro(
          "A Amazon recusou a classificação de preparo deste SKU. Use \"Atualizar da Amazon\" e revise o item."
        );
        return false;
      }
    }
    return false;
  }

  /**
   * "Atualizar da Amazon": re-consulta TODOS os SKUs (scope ALL) e ACOMPANHA a
   * resposta. O sync é assíncrono (task) — ler uma única vez logo após o POST
   * devolvia o estado velho e obrigava o operador a ficar reclicando às cegas.
   * Como o scope ALL regrava os prep details, as confirmações são limpas e
   * precisam ser refeitas (dado novo da Amazon ⇒ nova confirmação).
   */
  async function atualizar(): Promise<void> {
    setRefreshing(true);
    setErro(null);
    setAviso(null);
    // Um sync completo re-abre os SKUs: libera o auto-sync para agir de novo se
    // a Amazon deixar algum item para trás.
    autoSyncKeyRef.current = null;
    try {
      await api.refreshPrepDetails(planId, "ALL");
      if (alive.current) {
        setAviso("Consultando a preparação dos SKUs na Amazon…");
      }
      const mskus = selection.map((r) => r.msku);
      const completo = await pollarPrepDetails((items) =>
        prepDetailsResolvidos(items, mskus)
      );
      if (alive.current) {
        setAviso(
          completo !== null
            ? "Preparação atualizada com a resposta da Amazon. Confirme cada SKU para avançar."
            : 'A Amazon ainda não respondeu para todos os SKUs. Clique em "Atualizar da Amazon" novamente em alguns segundos.'
        );
      }
    } catch (error: unknown) {
      if (alive.current) {
        setErro(error instanceof Error ? error.message : "Falha ao atualizar da Amazon.");
      }
    } finally {
      if (alive.current) {
        setRefreshing(false);
      }
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Card
        title="Inventário a enviar"
        actions={
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {editable ? (
              <Button variant="secondary" onClick={() => setAddOpen(true)}>
                Adicionar SKU do inventário
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => void atualizar()}
              disabled={refreshing || sincronizandoPrep}
            >
              {refreshing ? "Atualizando…" : "Atualizar da Amazon"}
            </Button>
          </div>
        }
      >
        <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          Origem: <strong>Prep Center Bristol</strong>.{" "}
          {editable
            ? "Selecione os produtos do inventário, ajuste quantidade/etiquetagem e salve."
            : "Plano já registrado — produtos em modo somente leitura."}
        </p>
      </Card>

      {erro !== null ? (
        <p role="alert" style={{ color: "var(--orange)" }}>{erro}</p>
      ) : null}
      {carregando ? <p role="status">Carregando produtos…</p> : null}
      {sincronizandoPrep ? (
        <p role="status" style={{ fontSize: 12.5, color: "var(--blue)" }}>
          Consultando a preparação dos SKUs na Amazon… isso leva alguns segundos.
        </p>
      ) : null}
      {aviso !== null ? (
        <p role="status" style={{ fontSize: 12, color: "var(--text-muted)" }}>{aviso}</p>
      ) : null}

      {!carregando && selection.length === 0 ? (
        <Card>
          <p style={{ color: "var(--text-muted)" }}>
            {editable
              ? 'Nenhum produto selecionado. Use "Adicionar SKU do inventário".'
              : "Este plano não tem itens."}
          </p>
        </Card>
      ) : null}

      {selection.map((row) => {
        const p = prepByMsku.get(row.msku) ?? null;
        const inv = inventory.get(row.msku) ?? null;
        const confirmed = p?.confirmedAt != null;
        const title = row.productName || inv?.productName || row.msku;
        const asin = p?.asin || row.asin || inv?.asin || "—";
        const pendencias = inv?.complianceIssues ?? [];
        return (
          <Card key={row.msku}>
            {/*
              AVISO PREVENTIVO (incidente 959630fb): a Amazon publica em
              `Listings issues` as pendências do anúncio — foi exatamente aí que
              o SKU recusado com FBA_INB_0245 aparecia sinalizado ANTES do envio.
              Mostrar aqui evita que o operador só descubra o problema depois de
              o plano falhar. É um AVISO, não um bloqueio: a Amazon aceita boa
              parte dos envios mesmo com pendência registrada.
            */}
            {pendencias.length > 0 ? (
              <div
                role="alert"
                style={{
                  border: "1px solid var(--amber)",
                  background: "rgba(154,107,18,0.10)",
                  borderRadius: 9,
                  padding: "0.6rem 0.7rem",
                  marginBottom: "0.7rem",
                  fontSize: 12.5,
                  color: "var(--amber)"
                }}
              >
                <strong>
                  Atenção: a Amazon registra {pendencias.length === 1 ? "uma pendência" : `${pendencias.length} pendências`} neste
                  anúncio — o envio pode ser recusado.
                </strong>
                <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.1rem" }}>
                  {pendencias.map((issue, i) => (
                    <li key={`${issue.code}-${i}`}>
                      {issue.message}
                      {issue.code.length > 0 ? ` (código ${issue.code})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.85rem", flexWrap: "wrap" }}>
              <ProductImage src={inv?.imageUrl ?? ""} label={title} size={44} />
              <div style={{ flex: "1 1 200px", minWidth: 180 }}>
                <div style={{ fontWeight: 600 }}>{title}</div>
                <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {row.msku} · {asin}
                </div>
              </div>
              <ReadOnlySelect label="Modelo de embalagem" value="Unidades individuais" />
              <OwnerSelect
                value={row.labelOwner}
                editable={editable}
                onChange={(owner) => patchRow(row.msku, { labelOwner: owner })}
              />
              <QuantityField
                value={row.quantity}
                editable={editable}
                onChange={(quantity) => patchRow(row.msku, { quantity })}
              />
              <ExpirationField
                value={row.expiration}
                editable={editable}
                onChange={(expiration) => patchRow(row.msku, { expiration })}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", alignItems: "center" }}>
                <button
                  type="button"
                  aria-label="Consulte o modelo de embalagem"
                  title="Consulte o modelo de embalagem"
                  onClick={() => setModalRow(row)}
                  style={iconButtonStyle}
                >
                  👁
                </button>
                {editable ? (
                  <button
                    type="button"
                    aria-label={`Remover ${row.msku}`}
                    title="Remover"
                    onClick={() => removeRow(row.msku)}
                    style={{ ...iconButtonStyle, color: "var(--orange)" }}
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>

            <div
              style={{
                marginTop: "0.75rem",
                padding: "0.75rem",
                borderRadius: 9,
                background: "var(--panel-alt)"
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: "0.4rem" }}>
                Informações / ação · Retorno do Seller Central
              </div>
              {p === null ? (
                <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {dirty
                    ? "Salve os produtos para carregar a preparação."
                    : sincronizandoPrep
                      ? "Consultando a preparação deste SKU na Amazon…"
                      : 'Sem prep details. Use "Atualizar da Amazon".'}
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                  <div style={{ fontSize: 12.5 }}>
                    {p.prepCategory === "UNKNOWN"
                      ? "Sem classificação de preparo na Amazon — será registrada como \"sem preparo\" ao confirmar."
                      : p.prepCategory !== null
                        ? `Preparação: ${p.prepCategory}`
                        : "Não é necessário preparação adicional."}
                    {p.prepTypes.length > 0 ? ` (${p.prepTypes.join(", ")})` : ""}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--blue)",
                      background: "var(--blue-soft)",
                      borderRadius: 7,
                      padding: "0.4rem 0.6rem"
                    }}
                  >
                    Qualificado para economia nas tarifas de logística /
                    Cadastrar-se em Envio na embalagem do produto
                  </div>
                  <div style={{ fontSize: 12 }}>
                    Etiquetagem de unidade: {labelOwnerLabel(p.labelOwner)}
                  </div>
                  <div>
                    {confirmed ? (
                      <Badge tone="green">✓ Preparação confirmada</Badge>
                    ) : (
                      <Button
                        onClick={() => void confirmar(row.msku)}
                        disabled={confirmando !== null}
                      >
                        {confirmando === row.msku
                          ? "Confirmando na Amazon…"
                          : "Confirmar preparação"}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        );
      })}

      {selErrors.length > 0 ? (
        <div role="alert" style={{ color: "var(--orange)", fontSize: 12.5 }}>
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {selErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/*
        Aviso soft de FEFO: a Amazon NÃO expõe em listPrepDetails se um SKU é
        "datado", então o FiCC não tem como saber com certeza — mas item datado
        sem validade é recusado no createInboundPlan (FBA_INB_0180). Lembrar o
        operador aqui é mais barato do que descobrir isso na falha do Passo 2.
      */}
      {editable && selection.length > 0 && selection.every((r) => r.expiration === "") ? (
        <p style={{ fontSize: 12.5, color: "var(--amber)", margin: 0 }}>
          Nenhum produto tem validade preenchida. Se algum deles for item datado
          (FEFO), a Amazon recusa o envio sem essa data — preencha a validade
          antes de avançar.
        </p>
      ) : null}

      <Card>
        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "center", fontSize: 12.5 }}>
          <span>SKUs: <strong>{selection.length}</strong></span>
          <span>Unidades: <strong>{totalUnits}</strong></span>
          <span style={{ color: allConfirmed ? "var(--green)" : "var(--text-muted)", fontWeight: 700 }}>
            Prep confirmado {confirmedCount}/{selection.length}
          </span>
          {editable && dirty ? (
            <Badge tone="amber">alterações não salvas</Badge>
          ) : null}
          <div style={{ flex: 1 }} />
          {editable ? (
            <Button onClick={() => void salvar()} disabled={saving || !dirty}>
              {saving ? "Salvando…" : "Salvar produtos"}
            </Button>
          ) : null}
        </div>
      </Card>

      <PackingModeloModal
        msku={modalRow?.msku ?? null}
        asin={modalRow?.asin ?? null}
        prep={modalRow !== null ? prepByMsku.get(modalRow.msku) ?? null : null}
        imageUrl={modalRow !== null ? inventory.get(modalRow.msku)?.imageUrl ?? "" : ""}
        productName={
          modalRow !== null
            ? modalRow.productName || inventory.get(modalRow.msku)?.productName || ""
            : ""
        }
        onClose={() => setModalRow(null)}
      />

      {plan !== null ? (
        <AdicionarSkuModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          marketplaceId={plan.marketplaceId}
          sellerId={plan.sellerId}
          existingMskus={new Set(selection.map((r) => r.msku))}
          onAdd={addRows}
        />
      ) : null}
    </div>
  );
}

function ReadOnlySelect({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <label style={{ display: "inline-block" }}>
      <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>{label}</div>
      <select value={value} disabled aria-label={label} style={{ minWidth: 140 }}>
        <option>{value}</option>
      </select>
    </label>
  );
}

function OwnerSelect({
  value,
  editable,
  onChange
}: {
  value: ItemOwner;
  editable: boolean;
  onChange: (owner: ItemOwner) => void;
}): ReactNode {
  if (!editable) {
    return <ReadOnlySelect label="Quem etiqueta" value={labelOwnerLabel(value)} />;
  }
  return (
    <label style={{ display: "inline-block" }}>
      <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>Quem etiqueta</div>
      <select
        value={value}
        aria-label="Quem etiqueta"
        onChange={(e) => onChange(e.target.value as ItemOwner)}
        style={{ minWidth: 140 }}
      >
        <option value="SELLER">Pelo vendedor</option>
        <option value="AMAZON">Pela Amazon</option>
      </select>
    </label>
  );
}

function QuantityField({
  value,
  editable,
  onChange
}: {
  value: number;
  editable: boolean;
  onChange: (quantity: number) => void;
}): ReactNode {
  return (
    <div style={{ textAlign: "right" }}>
      <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>Quantidade</div>
      {editable ? (
        <input
          type="number"
          min={1}
          value={value}
          aria-label="Quantidade"
          onChange={(e) => onChange(Math.max(1, Number(e.target.value) || 1))}
          style={{ width: 80, textAlign: "right" }}
        />
      ) : (
        <div style={{ fontSize: 15, fontWeight: 700 }}>{value}</div>
      )}
    </div>
  );
}

// Validade (FEFO). A SP-API exige `expiration` em itens datados no
// createInboundPlan e a rejeita em itens não-datados — por isso o campo é
// sempre opcional e o operador só preenche para os SKUs datados. Não há flag
// confiável de "datado" em listPrepDetails, então o input fica sempre visível.
function ExpirationField({
  value,
  editable,
  onChange
}: {
  value: string;
  editable: boolean;
  onChange: (expiration: string) => void;
}): ReactNode {
  if (!editable) {
    return (
      <div>
        <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>Validade</div>
        <div style={{ fontSize: 13 }}>{value || "—"}</div>
      </div>
    );
  }
  return (
    <label style={{ display: "inline-block" }}>
      <div
        style={{ fontSize: 10.5, color: "var(--text-muted)" }}
        title="Apenas itens datados (FEFO); deixe em branco para os demais."
      >
        Validade (FEFO)
      </div>
      <input
        type="date"
        value={value}
        aria-label="Validade (apenas itens datados/FEFO)"
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 150 }}
      />
    </label>
  );
}

const iconButtonStyle = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: 16,
  color: "var(--blue)"
} as const;
